import {
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import {
    isExternalOrderingBlock,
    orthopulseBlockedMessage,
    orthopulseBlockedTone,
    type OrthopulseBlockedTone,
    type OrthopulseOrderBlocked
} from '../../services/orthopulseOrder';
import type { PurchaseOrderChannel } from '../../services/purchaseOrders';

const TONE_CLASSES: Record<OrthopulseBlockedTone, string> = {
    info: 'border-blue-300 bg-blue-50 text-blue-900',
    warning: 'border-amber-300 bg-amber-50 text-amber-900',
    error: 'border-red-300 bg-red-50 text-red-900'
};

const TONE_ICONS: Record<OrthopulseBlockedTone, typeof XCircleIcon> = {
    info: InformationCircleIcon,
    warning: ExclamationTriangleIcon,
    error: XCircleIcon
};

const TONE_ICON_CLASSES: Record<OrthopulseBlockedTone, string> = {
    info: 'text-blue-700',
    warning: 'text-amber-700',
    error: 'text-red-700'
};

interface OrthopulseBlockedBannerProps {
    blocked: NonNullable<OrthopulseOrderBlocked>;
}

// Tone comes from orthopulseBlockedTone, not `level`: an external-ordering
// block is level 'error' but is an actionable next step, not a failure.
export function OrthopulseBlockedBanner({ blocked }: OrthopulseBlockedBannerProps) {
    const tone = orthopulseBlockedTone(blocked);
    const Icon = TONE_ICONS[tone];

    return (
        <div className={`flex items-start gap-3 p-4 rounded-xl border ${TONE_CLASSES[tone]}`}>
            <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${TONE_ICON_CLASSES[tone]}`} />
            <div className="min-w-0">
                <div className="text-sm font-medium">{orthopulseBlockedMessage(blocked)}</div>
                {isExternalOrderingBlock(blocked) && (
                    <div className="text-xs mt-1">
                        Once ordered, open the purchase order and use “Mark placed”.
                    </div>
                )}
            </div>
        </div>
    );
}

// An external order's channel is a fact about the product, not about whether
// anything is blocked - so it reads correctly even when `blocked` is null.
function ExternalChannelNotice() {
    return (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-blue-300 bg-blue-50 text-blue-900">
            <InformationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-700" />
            <div className="min-w-0">
                <div className="text-sm font-medium">
                    Awaiting placement. This product is ordered on the vendor's website, so no email
                    is sent.
                </div>
                <div className="text-xs mt-1">
                    Once ordered, open the purchase order and use “Mark placed”.
                </div>
            </div>
        </div>
    );
}

function PatientPaidNotice() {
    return (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-blue-300 bg-blue-50 text-blue-900">
            <InformationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-700" />
            <div className="min-w-0">
                <div className="text-sm font-medium">
                    Paid by the patient. The practice will not be charged.
                </div>
                <div className="text-xs mt-1">
                    The patient already paid ODO, so Billing records a $0 charge that is never
                    invoiced to the practice.
                </div>
            </div>
        </div>
    );
}

interface OrthopulseOrderNoticeProps {
    ordersVia: PurchaseOrderChannel;
    blocked: OrthopulseOrderBlocked;
    practiceCharged?: boolean;
}

// One notice per preview: a block explains itself, and an unblocked external
// order still needs to say that nothing will be emailed.
export function OrthopulseOrderNotice({
    ordersVia,
    blocked,
    practiceCharged
}: OrthopulseOrderNoticeProps) {
    if (blocked) return <OrthopulseBlockedBanner blocked={blocked} />;
    if (ordersVia === 'external') return <ExternalChannelNotice />;
    if (practiceCharged === false) return <PatientPaidNotice />;
    return null;
}
